/**
 * Cloudflare Turnstile widget.
 *
 * The shared fluidvip GoTrue enforces Turnstile on password sign-in, so a login
 * without a token is rejected — this is not optional decoration.
 *
 * The script is loaded once per page and the widget rendered explicitly, so we
 * control when it mounts. A token is single-use: after a failed attempt the
 * parent bumps `resetKey` to remount and get a fresh challenge.
 */
import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const SCRIPT_ID = 'cf-turnstile-script';
const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve) => existing.addEventListener('load', () => resolve(), { once: true }));
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Turnstile failed to load')), { once: true });
    document.head.appendChild(script);
  });
}

export interface TurnstileProps {
  siteKey: string;
  onToken: (token: string | null) => void;
  /** Change to force a fresh challenge. */
  resetKey?: number;
}

export default function Turnstile({ siteKey, onToken, resetKey = 0 }: TurnstileProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host) return;

    void loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || !hostRef.current) return;
        host.innerHTML = '';
        widgetRef.current = window.turnstile.render(host, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      })
      .catch(() => onToken(null));

    return () => {
      cancelled = true;
      if (widgetRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetRef.current); } catch { /* already gone */ }
        widgetRef.current = null;
      }
    };
    // onToken is stable enough in practice; resetKey is the intentional remount trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, resetKey]);

  return <div ref={hostRef} style={{ minHeight: 65 }} />;
}
